// Detalhe completo de uma submissão: código, resultado por teste e rejulgar.

import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw, ServerCrash } from "lucide-react";
import type { AdminSubmissionDetailDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import {
  formatContestTime,
  formatDateTime,
  formatMemory,
  formatTime,
} from "../../lib/format.js";
import { Button } from "../../components/motion/button/base";
import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { CodeBlock, Loading, VerdictBadge } from "../../components/domain.js";
import { Identicon } from "../../components/Identicon.js";

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminSubmissionDetailPage(): React.JSX.Element {
  const confirmAlert = useAlertConfirm();
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const submission = useQuery({
    queryKey: ["admin-submission", id],
    queryFn: () => api.get<AdminSubmissionDetailDto>(`/admin/submissions/${id}`),
  });

  const rejudge = useMutation({
    mutationFn: () => api.post(`/admin/submissions/${id}/rejudge`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-submission", id] });
      await queryClient.invalidateQueries({ queryKey: ["admin-submissions"] });
    },
  });

  if (submission.isPending) return <Loading label="Carregando a submissão…" />;
  if (submission.error) {
    return (
      <div className="space-y-4">
        <AlertMessage tone="error">{errorMessage(submission.error)}</AlertMessage>
        <Link to="/admin/submissoes" className="text-sm text-accent hover:underline">
          Voltar para as submissões
        </Link>
      </div>
    );
  }

  const s = submission.data;

  return (
    <div className="space-y-7">
      <div>
        <Link
          to="/admin/submissoes"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={15} aria-hidden />
          Submissões
        </Link>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <Identicon seed={s.teamId} className="size-11" />
          <div className="min-w-0">
            <h1 className="truncate text-[28px] font-semibold tracking-tight">
              {s.teamName}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Problema {s.problemLabel} · {s.language} ·{" "}
              {formatContestTime(s.contestSeconds)} de prova
            </p>
          </div>
          <VerdictBadge status={s.status} verdict={s.verdict} size="lg" />

          <Button
            variant="secondary"
            className="ml-auto rounded-xl"
            disabled={rejudge.isPending}
            onClick={async () => {
              if (
                await confirmAlert(
                  "Rejulgar esta submissão? O resultado atual será substituído e, até o novo veredito, ela conta como pendente no placar.",
                )
              ) {
                rejudge.mutate();
              }
            }}
          >
            <RefreshCw
              size={15}
              className={rejudge.isPending ? "animate-spin" : ""}
              aria-hidden
            />
            {rejudge.isPending ? "Rejulgando…" : "Rejulgar"}
          </Button>
        </div>
      </div>

      {rejudge.error && (
        <AlertMessage tone="error">{errorMessage(rejudge.error)}</AlertMessage>
      )}
      {rejudge.isSuccess && (
        <AlertMessage>
          Enviada para a fila. Até o novo veredito, conta como pendente no placar.
        </AlertMessage>
      )}

      {s.status === "INTERNAL_ERROR" && (
        <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
          <ServerCrash size={18} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="text-sm leading-6">
            <p className="font-medium">Falha do sistema de julgamento</p>
            <p className="text-muted-foreground">
              O erro é da infraestrutura, não da equipe. Não conta como tentativa errada
              no placar. Rejulgue quando o Judge0 estiver saudável.
            </p>
          </div>
        </div>
      )}

      <section className="grid gap-5 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Enviada em" value={formatDateTime(s.submittedAt)} />
        <Fact label="Tempo de prova" value={formatContestTime(s.contestSeconds)} />
        <Fact label="Tempo (pior caso)" value={formatTime(s.timeMs)} />
        <Fact label="Memória (pior caso)" value={formatMemory(s.memoryKb)} />
        <Fact label="Tentativas de julgamento" value={String(s.judgeAttempts)} />
        <Fact
          label="Primeiro teste que falhou"
          value={s.failedTestPosition ? `#${s.failedTestPosition}` : "—"}
        />
        <Fact
          label="Julgada em"
          value={s.judgedAt ? formatDateTime(s.judgedAt) : "—"}
        />
        <Fact label="Linguagem" value={s.language} />
      </section>

      {s.compileOutput && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium">Saída do compilador</h2>
          <CodeBlock tone="error" maxHeight="max-h-72">
            {s.compileOutput}
          </CodeBlock>
        </section>
      )}

      {s.results.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-border bg-card">
          <header className="border-b border-border px-5 py-4">
            <h2 className="text-sm font-medium">Resultado por caso de teste</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Visível apenas para administradores. A equipe recebe só o veredito final.
            </p>
          </header>

          <table className="w-full text-sm">
            <thead className="border-b border-border text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="w-16 px-5 py-3 text-left font-medium">
                  #
                </th>
                <th scope="col" className="px-5 py-3 text-left font-medium">
                  Veredito
                </th>
                <th scope="col" className="w-28 px-5 py-3 text-right font-medium">
                  Tempo
                </th>
                <th scope="col" className="w-28 px-5 py-3 text-right font-medium">
                  Memória
                </th>
                <th scope="col" className="px-5 py-3 text-left font-medium">
                  stderr
                </th>
              </tr>
            </thead>
            <tbody>
              {s.results.map((r) => (
                <tr key={r.position} className="border-b border-border last:border-0">
                  <td className="px-5 py-3 font-mono text-muted-foreground">
                    {r.position}
                  </td>
                  <td className="px-5 py-3">
                    <VerdictBadge status="DONE" verdict={r.verdict} size="sm" />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {formatTime(r.timeMs)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {formatMemory(r.memoryKb)}
                  </td>
                  <td className="max-w-xs px-5 py-3">
                    {r.stderr ? (
                      <code className="block truncate text-xs text-destructive" title={r.stderr}>
                        {r.stderr}
                      </code>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-medium">Código enviado</h2>
        <CodeBlock maxHeight="max-h-[32rem]">{s.sourceCode}</CodeBlock>
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 font-medium">{value}</dd>
    </div>
  );
}
